import React from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import {AutomationTransformFileGraphQL} from 'lab-automation/automation-files/LabAutomationConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import AutomationFileStatusModal, {
  isAutomationInputFile,
  isAutomationOutputFile,
} from './AutomationFileStatusModal';

interface ShowAutomationFileErrorsButtonProps {
  automationFile: AutomationOutputFileGraphQL | AutomationInputFileGraphQL;
  runId: string | null;
  configName: string | null;
  locked: boolean;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFileGraphQL>) => void;
  updateAutomationInputFile: (automationInputFile: AutomationInputFileGraphQL) => void;
  previousFileNames: Set<string>;
}

function getNumErrors(
  automationFile: AutomationOutputFileGraphQL | AutomationInputFileGraphQL | AutomationTransformFileGraphQL
): number {
  let numErrors = 0;
  if (automationFile.errors != null) {
    for (const errorList of Object.values(automationFile.errors)) {
      numErrors += (errorList as Array<{errorMessage: string}>).length;
    }
  }
  if (isAutomationOutputFile(automationFile) && automationFile.transforms != null) {
    for (const transform of automationFile.transforms) {
      if (transform) {
        numErrors += getNumErrors(transform);
      }
    }
  }
  if (isAutomationInputFile(automationFile) && automationFile.transformFiles != null) {
    for (const transformFile of automationFile.transformFiles) {
      if (transformFile) {
        numErrors += getNumErrors(transformFile);
      }
    }
  }
  return numErrors;
}

const ShowAutomationFileErrorsButton: React.FunctionComponent<ShowAutomationFileErrorsButtonProps> = (
  props: ShowAutomationFileErrorsButtonProps
): JSX.Element => {
  const numErrors = getNumErrors(props.automationFile);
  const editorPropertiesContext = EditorPropertiesContext.useContext();
  return (
    <Button
      buttonStyle={ButtonStyle.NEGATIVE}
      size={ButtonSize.MINI}
      fullWidth={true}
      text={`Show ${numErrors} ${benchlingUtil.pluralize(numErrors, 'error', 'errors')}`}
      icon="warning"
      disabled={numErrors === 0}
      onClick={() => {
        runModal(AutomationFileStatusModal, {
          automationFile: props.automationFile,
          runId: props.runId,
          configName: props.configName,
          locked: props.locked,
          updateAutomationOutputFiles: props.updateAutomationOutputFiles,
          updateAutomationInputFile: props.updateAutomationInputFile,
          previousFileNames: props.previousFileNames,
          editorPropertiesContext,
        });
      }}
    />
  );
};

export default ShowAutomationFileErrorsButton;
